import styled, { keyframes } from 'styled-components/macro';

import { rem } from '@blueprint/ui';

import { Icon, Text } from 'components/blueprint';
import { colors, breakpoints, oneColumnLayout } from 'themes';
import { useSafeLabel } from 'hooks';
import {
  useIsSupportPanelOpen,
  useSetIsSupportPanelOpen,
} from 'state/isSupportPanelOpen';
import { useAppConfig } from 'state/appConfig';
import { useConversation } from 'state/conversation';

import { useInteractionTracking } from 'tracking/hooks/useInteractionTracking';

import messages from './messages';
import { getAnimationFrames } from './getAnimationFrames';
import { useCheckNewMessagesOnPanelClose } from './useCheckNewMessagesOnPanelClose';
import { useGenesysBootstrap } from 'hooks/useGenesysBootstrap';

export const PANEL_TRIGGER_BUTTON_ID =
  'get-support-panel-display-trigger-button';

const { pink, white } = colors;
const { frames, duration } = getAnimationFrames();

const shake = keyframes`
  ${frames}
`;

const Button = styled.button`
  display: flex;
  flex-direction: column;
  gap: ${rem(8)};
  align-items: center;
  padding: ${rem(16)} ${rem(10)};
  background-color: ${pink};
  cursor: pointer;
  border: ${pink};

  &.dropped {
    animation-name: ${shake};
    animation-duration: ${duration}s;
    animation-iteration-count: 3;
  }

  &.lifted {
    padding-top: ${rem(44)};
    padding-bottom: ${rem(44)};
  }

  @media (max-width: ${breakpoints.md}px) {
    &.lifted {
      display: none;
    }
  }

  @media print {
    display: none;
  }
`;

const BadgeIcon = styled.div`
  height: ${rem(10)};
  width: ${rem(10)};
  position: absolute;
  top: ${rem(15)};
  left: ${rem(20)};
  border-radius: 50%;
  background-color: ${white};
  border: ${rem(1.5)} solid;
  border-color: ${pink};
  z-index: 10;
`;

const CHANNEL_DBBUK = 'dbbuk';

/**
 * GetSupportButton
 * Creates a button
 * and is useful for accessible Icons
 */
const GetSupportButton = () => {
  const safeLabel = useSafeLabel();

  const isSupportPanelOpen = useIsSupportPanelOpen();
  const setIsSupportPanelOpened = useSetIsSupportPanelOpen();
  const { channel } = useAppConfig();
  const conversation = useConversation();

  useGenesysBootstrap();

  const trackButton = useInteractionTracking('button click');

  const hasNewMessagesOnPanelClose = useCheckNewMessagesOnPanelClose(
    isSupportPanelOpen,
    conversation,
  );

  const getLabelPerChannel = () => {
    if (hasNewMessagesOnPanelClose) {
      return messages.newMessage;
    }

    const lowerCaseChannel = channel?.toLowerCase();

    if (!lowerCaseChannel) {
      return {};
    }

    if (lowerCaseChannel === CHANNEL_DBBUK) {
      return messages.dbbuk;
    } else {
      return messages.getSupport;
    }
  };

  const channelLabel = getLabelPerChannel();

  return (
    <>
      {channel && (
        <Button
          id={PANEL_TRIGGER_BUTTON_ID}
          data-testid={PANEL_TRIGGER_BUTTON_ID}
          className={isSupportPanelOpen ? 'lifted' : 'dropped'}
          onClick={trackButton({
            onClick: () => {
              setIsSupportPanelOpened(!isSupportPanelOpen);
            },
            tealiumData: {
              event_content: `${document.title}: need help`,
            },
          })}
          aria-label={
            isSupportPanelOpen ? safeLabel(messages.hideAriaLabel) : null
          }
        >
          {!isSupportPanelOpen && (
            <>
              {hasNewMessagesOnPanelClose && <BadgeIcon />}
              <Icon name="ChatAi" fill="white" />
            </>
          )}
          <Text
            mode="dark"
            align="center"
            lineHeight={rem(18)}
            css={`
               {
                && {
                  writing-mode: vertical-rl;
                  display: flex;

                  ${oneColumnLayout} {
                    display: none;
                  }
                }
              }
            `}
          >
            {safeLabel(!isSupportPanelOpen ? channelLabel : messages.hide)}
          </Text>
        </Button>
      )}
    </>
  );
};

export default GetSupportButton;
